import { useState, useEffect, useCallback, useRef } from 'react';

const SW_PATH = '/firebase-messaging-sw.js';
const UPDATE_CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes (fallback)

// --- Soft-prompt-only version notifier -----------------------------------------------
// We track dismiss history to schedule re-prompts, but forceBlocking is NEVER set.
// The blocking modal has been removed — only the soft "Update Available" toast is shown.
//   - DISMISSED_VERSION_KEY: which version was last dismissed
//   - DISMISS_COUNT_KEY: how many times the user has dismissed THIS version
//   - DISMISS_LAST_AT_KEY: timestamp (ms) of the most recent dismiss for this version
//   - FIRST_DETECTED_AT_KEY: timestamp (ms) we first detected this version on this tab/origin
// After each dismiss we schedule a re-prompt RE_PROMPT_INTERVAL_MS later.
const DISMISSED_VERSION_KEY = 'dismissed_sw_version';
const DISMISS_COUNT_KEY = 'dismissed_sw_version_count';
const DISMISS_LAST_AT_KEY = 'dismissed_sw_version_last_at';
const FIRST_DETECTED_AT_KEY = 'sw_version_first_detected_at';

// Configurable for tests via window.__VERSION_CHECK_OVERRIDES__ (smoke testing only)
const getConfig = () => {
    const overrides = (typeof window !== 'undefined' && window.__VERSION_CHECK_OVERRIDES__) || {};
    return {
        rePromptIntervalMs: overrides.rePromptIntervalMs ?? 15 * 60 * 1000, // 15 min
    };
};

/**
 * Hook to check for new application versions using Service Worker.
 * Also monitors online/offline status.
 *
 * Soft-prompt-only policy:
 *   1. Detect new version → soft "Update Available" toast (bottom-left).
 *   2. User clicks Later → toast closes, re-shown 15 minutes later (indefinitely).
 *   3. forceBlocking is ALWAYS false — the blocking modal no longer exists.
 *   4. ChunkLoadError from a route lazy import (see src/routes/lazyRetry.jsx) calls
 *      window.__forceVersionBlockingModal__() which now only surfaces the soft toast;
 *      it does NOT set forceBlocking.
 *
 * @returns {Object} { hasNewVersion, currentVersion, newVersion, refreshApp, dismissUpdate, isOffline, forceBlocking }
 */
// Helper to get version from a service worker via MessageChannel
const getVersionFromSW = (sw) =>
    new Promise((resolve) => {
        const messageChannel = new MessageChannel();
        const timeout = setTimeout(() => resolve(null), 1000);
        messageChannel.port1.onmessage = (event) => {
            clearTimeout(timeout);
            resolve(event.data?.version || null);
        };
        try {
            sw.postMessage({ type: 'GET_VERSION' }, [messageChannel.port2]);
        } catch {
            clearTimeout(timeout);
            resolve(null);
        }
    });

const readInt = (key) => {
    try {
        const v = parseInt(localStorage.getItem(key) || '', 10);
        return Number.isFinite(v) ? v : 0;
    } catch {
        return 0;
    }
};

export default function useVersionCheck() {
    const [hasNewVersion, setHasNewVersion] = useState(false);
    const [currentVersion, setCurrentVersion] = useState(null);
    const [newVersion, setNewVersion] = useState(null);
    const [registration, setRegistration] = useState(null);
    const [isOffline, setIsOffline] = useState(!navigator.onLine);
    // forceBlocking is always false — the blocking modal has been removed.
    // Kept in the return value for API compatibility.
    const forceBlocking = false;
    const waitingWorkerRef = useRef(null);
    const waitingVersionRef = useRef(null);
    const rePromptTimerRef = useRef(null);

    // Monitor online/offline status
    useEffect(() => {
        const handleOnline = () => setIsOffline(false);
        const handleOffline = () => setIsOffline(true);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    // Register Service Worker and set up update detection
    useEffect(() => {
        if (!('serviceWorker' in navigator)) {
            console.warn('[VersionCheck] Service Workers not supported');
            return undefined;
        }

        let mounted = true;

        const showPromptForWorker = async (worker) => {
            const version = await getVersionFromSW(worker);
            if (!version || !mounted) return;
            waitingWorkerRef.current = worker;
            waitingVersionRef.current = version;

            // Track first-detected timestamp per version.
            const dismissedFor = localStorage.getItem(DISMISSED_VERSION_KEY);
            if (dismissedFor !== version) {
                // Brand-new version → reset all per-version counters.
                localStorage.setItem(DISMISSED_VERSION_KEY, version);
                localStorage.setItem(DISMISS_COUNT_KEY, '0');
                localStorage.setItem(DISMISS_LAST_AT_KEY, '0');
                localStorage.setItem(FIRST_DETECTED_AT_KEY, String(Date.now()));
            } else if (!readInt(FIRST_DETECTED_AT_KEY)) {
                localStorage.setItem(FIRST_DETECTED_AT_KEY, String(Date.now()));
            }

            setNewVersion(version);
            setHasNewVersion(true);
        };

        const registerSW = async () => {
            try {
                const reg = await navigator.serviceWorker.register(SW_PATH);

                if (!mounted) return;

                setRegistration(reg);

                // Check if there's already a waiting worker (update available)
                if (reg.waiting) {
                    showPromptForWorker(reg.waiting);
                }

                // Listen for new updates
                reg.addEventListener('updatefound', () => {
                    const newWorker = reg.installing;

                    if (!newWorker) return;

                    newWorker.addEventListener('statechange', () => {
                        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                            showPromptForWorker(newWorker);
                        }
                    });
                });

                // Get current version from active SW
                if (reg.active) {
                    const version = await getVersionFromSW(reg.active);
                    if (version) {
                        setCurrentVersion(version);
                    }
                }
            } catch (err) {
                console.error('[VersionCheck] SW registration failed:', err);
            }
        };

        registerSW();

        // Listen for SW controller change (when new SW takes over)
        const handleControllerChange = () => {
            // New SW is now controlling the page, reload to get new content
            window.location.reload();
        };

        navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

        // Global escape hatch for chunk-load failures from route lazy imports.
        // src/routes/lazyRetry.jsx calls this. Previously this triggered the blocking
        // modal; now it surfaces the soft "Update Available" toast instead.
        const showSoftToastFromExternal = () => {
            setHasNewVersion(true);
            // forceBlocking is never set — soft toast only.
        };
        window.__forceVersionBlockingModal__ = showSoftToastFromExternal;

        return () => {
            mounted = false;
            navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
            if (window.__forceVersionBlockingModal__ === showSoftToastFromExternal) {
                delete window.__forceVersionBlockingModal__;
            }
            if (rePromptTimerRef.current) {
                clearTimeout(rePromptTimerRef.current);
                rePromptTimerRef.current = null;
            }
        };
    }, []);

    // Periodic check for updates (fallback, browser does this automatically but we can be proactive)
    useEffect(() => {
        if (!registration) return undefined;

        const checkForUpdate = () => {
            registration.update().catch(() => {
                // Ignore update check errors
            });
        };

        // Check on visibility change (when user returns to tab)
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                checkForUpdate();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Also check periodically as fallback
        const intervalId = setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            clearInterval(intervalId);
        };
    }, [registration]);

    // The hard absolute-deadline watchdog (setForceBlocking after 60 min) has been
    // removed. forceBlocking is never set — only the soft toast is shown.

    // Refresh the app - tell waiting SW to take over.
    // Also clear per-version dismiss counters so the next deploy starts fresh.
    const refreshApp = useCallback(() => {
        const waitingWorker = waitingWorkerRef.current;

        localStorage.removeItem(DISMISSED_VERSION_KEY);
        localStorage.removeItem(DISMISS_COUNT_KEY);
        localStorage.removeItem(DISMISS_LAST_AT_KEY);
        localStorage.removeItem(FIRST_DETECTED_AT_KEY);

        if (waitingWorker) {
            waitingWorker.postMessage({ type: 'SKIP_WAITING' });
        }

        // Clear caches
        if ('caches' in window) {
            caches.keys().then((names) => {
                names.forEach((name) => {
                    caches.delete(name);
                });
            });
        }

        // The controllerchange event will trigger a reload.
        // If it doesn't fire quickly (e.g. there is no waiting worker because we got here
        // via the chunk-load escape hatch), force a hard reload.
        setTimeout(() => {
            try {
                window.location.reload(true);
            } catch {
                window.location.reload();
            }
        }, 1000);
    }, []);

    // Dismiss the update notification — always re-prompts in 15 min, never blocks.
    const dismissUpdate = useCallback(() => {
        const version = waitingVersionRef.current;
        const cfg = getConfig();

        if (version) {
            localStorage.setItem(DISMISSED_VERSION_KEY, version);
            const prev = readInt(DISMISS_COUNT_KEY);
            localStorage.setItem(DISMISS_COUNT_KEY, String(prev + 1));
            localStorage.setItem(DISMISS_LAST_AT_KEY, String(Date.now()));

            if (rePromptTimerRef.current) {
                clearTimeout(rePromptTimerRef.current);
                rePromptTimerRef.current = null;
            }

            // Always re-prompt after 15 min — never escalate to blocking.
            rePromptTimerRef.current = setTimeout(() => {
                // Re-show: flip hasNewVersion back true. The same waiting worker
                // is still in waitingWorkerRef.
                setHasNewVersion(true);
            }, cfg.rePromptIntervalMs);
        }
        setHasNewVersion(false);
        setNewVersion(version || null);
    }, []);

    return {
        hasNewVersion,
        currentVersion,
        newVersion,
        refreshApp,
        dismissUpdate,
        isOffline,
        forceBlocking,
    };
}
